import {
  AddColumnHistoryType,
  AddHistoryType,
  DeleteColumnHistoryType,
  DeleteHistoryType,
  EditColumnHistoryType,
  EditHistoryType,
  MergeHistoryType,
  MoveColumnHistoryType,
  PasteHistoryType,
  ResetHistoryType,
  RestoreHistoryType,
} from 'containers/Library/type/extract-text-type';
import {
  DagDatasetData,
  DagProjectData,
} from 'containers/ProjectFlows/APFDetailsModal/type/APFType';
import { OcrHistoryType } from 'stores/library/useOcrTextStore';
import {
  DateChatItemType,
  UserChatItemType,
  AssistantChatItemType,
  ChatItemType,
} from 'types/chatbot/chatbot';
import {
  ConnectorAbleOptionType,
  ConnectorBooleanOptionType,
  ConnectorJdbcOptionInfoType,
  ConnectorOptionInfoType,
  ConnectorSftpOptionInfoType,
  ConnectorVisibleOptionType,
  DatasourceOptionInfoType,
  DatasourceSftpOptionInfoType,
} from 'types/connectorAndDatasource/ConnectorAndDatasourceType';
import {
  LibraryExportFileDelimitedType,
  LibraryExportFileFixedType,
  LibraryExportFileJsonType,
  LibraryExportFileXmlType,
} from 'types/library/LibraryExportType';
import {
  LibraryFileDtoOptionType,
  LibraryImportPreviewDelimitedType,
  LibraryImportPreviewExcelType,
  LibraryImportPreviewFixedType,
  LibraryImportPreviewJsonType,
  LibraryImportPreviewXmlType,
  LibraryPreviewRequestFileType,
  LibraryPreviewRequestJdbcCreateQueryType,
  LibraryPreviewRequestJdbcType,
  LibraryPreviewRequestSftpWildcardType,
  LibrarySelectedDatasetAllType,
} from 'types/library/LibraryImportType';
import { DatasourceJdbcOptionInfoType } from 'types/library/LibraryType';
import {
  ChartObject,
  ChartRefObject,
  ContentsType,
  ShapeObject,
  TextObject,
} from 'types/report/ReportDataTypes';
import {
  BackgroundGradientType,
  BackgroundImageType,
  BackgroundSolidType,
  BackgroundType,
} from 'types/report/ReportStyleTypes';
import { OBJECT_TYPE } from 'types/report/enum';

export const isConnectorType = (
  obj: ConnectorOptionInfoType | DatasourceOptionInfoType
): obj is ConnectorOptionInfoType => {
  return (
    (obj as ConnectorOptionInfoType).connectorId !== undefined &&
    (obj as DatasourceOptionInfoType).datasourceName === undefined
  );
};

export const isConnectorJdbcType = (
  obj: ConnectorJdbcOptionInfoType | DatasourceJdbcOptionInfoType
): obj is ConnectorJdbcOptionInfoType => {
  return (
    (obj as ConnectorJdbcOptionInfoType).connectorJdbcDto !== undefined &&
    (obj as DatasourceJdbcOptionInfoType).datasourceJdbcDto === undefined
  );
};

export const isConnectorSftpType = (
  obj: ConnectorSftpOptionInfoType | DatasourceSftpOptionInfoType
): obj is ConnectorSftpOptionInfoType => {
  return (
    (obj as ConnectorSftpOptionInfoType).connectorSftpDto !== undefined &&
    (obj as DatasourceSftpOptionInfoType).datasourceSftpDto === undefined
  );
};

export const isConnectorVisibleOptionType = (
  obj: ConnectorVisibleOptionType
): obj is ConnectorVisibleOptionType => {
  return (
    (obj.label === 'Hide' && obj.value === 'HIDE') ||
    (obj.label === 'Show All' && obj.value === 'ALL') ||
    (obj.label === 'Show Only' && obj.value === 'ONLY')
  );
};

export const isConnectorBooleanOptionType = (
  obj: ConnectorBooleanOptionType
): obj is ConnectorBooleanOptionType => {
  return (
    (obj.label === 'True' && obj.value === true) || (obj.label === 'False' && obj.value === false)
  );
};

export const isConnectorAbleOptionType = (
  obj: ConnectorAbleOptionType
): obj is ConnectorAbleOptionType => {
  return (
    (obj.label === 'Enabled' && obj.value === true) ||
    (obj.label === 'Disabled' && obj.value === false)
  );
};

export const isDatasourceInfoType = (
  obj: ConnectorOptionInfoType | DatasourceOptionInfoType
): obj is DatasourceOptionInfoType => {
  return (obj as DatasourceOptionInfoType).datasourceName !== undefined;
};

// export const isDatasourceJdbcOptionInfoType = (
//   obj: DatasourceJdbcOptionInfoType | ConnectorJdbcOptionInfoType
// ): obj is DatasourceJdbcOptionInfoType => {
//   return (obj as DatasourceJdbcOptionInfoType).datasourceName !== undefined;
// };

export const isLibraryPreviewRequestJdbcType = (obj: any): obj is LibraryPreviewRequestJdbcType => {
  return obj && typeof obj === 'object' && 'connectorType' in obj && 'jdbcDto' in obj;
};

export const isLibraryPreviewRequestJdbcCreateQueryType = (
  obj: any
): obj is LibraryPreviewRequestJdbcCreateQueryType => {
  return (
    obj &&
    typeof obj === 'object' &&
    'connectorType' in obj &&
    'jdbcDto' in obj &&
    'query' in obj.jdbcDto
  );
};
export const isLibraryPreviewRequestSftpWildcardSearchType = (
  obj: any
): obj is LibraryPreviewRequestSftpWildcardType => {
  return (
    obj && typeof obj === 'object' && 'connectorType' in obj && 'fileDto' in obj && 'search' in obj
  );
};

export const isLibraryImportPreviewDelimitedType = (
  obj: any
): obj is LibraryImportPreviewDelimitedType => {
  return obj && typeof obj === 'object' && 'columnSeparator' in obj && 'lineSeparator' in obj;
};

export const isLibraryImportPreviewFixedType = (obj: any): obj is LibraryImportPreviewFixedType => {
  return typeof obj?.sameWidth === 'boolean';
};

export const isLibraryImportPreviewExcelType = (obj: any): obj is LibraryImportPreviewExcelType => {
  return typeof obj?.workSheet === 'string';
};

export const isLibraryImportPreviewXmlType = (obj: any): obj is LibraryImportPreviewXmlType => {
  return typeof obj?.columnNaming === 'string';
};

/** XML과 JSON 옵션이 같으므로 XML 타입가드 함수를 활용 */
export const isLibraryExportPreviewJsonType = (obj: any): obj is LibraryImportPreviewJsonType => {
  return isLibraryImportPreviewXmlType(obj);
};

export const isLibraryPreviewRequestFileType = (obj: any): obj is LibraryPreviewRequestFileType => {
  return (
    obj &&
    typeof obj === 'object' &&
    'connectorType' in obj &&
    'fileDto' in obj &&
    'fileFormat' in obj.fileDto
  );
};

export const isLibrarySelectedDatasetAllType = (obj: any): obj is LibrarySelectedDatasetAllType => {
  return (
    obj &&
    typeof obj === 'object' &&
    'connectorType' in obj &&
    ('jdbcDto' in obj || 'fileDto' in obj)
  );
};

export const isLibraryFileDtoOptionType = (obj: any): obj is LibraryFileDtoOptionType => {
  return (
    obj &&
    typeof obj === 'object' &&
    'fileFormat' in obj &&
    ('delimited' in obj ||
      'fixed' in obj ||
      'excel' in obj ||
      'avro' in obj ||
      'json' in obj ||
      'xml' in obj)
  );
};

export const isLibraryExportDelimitedType = (obj: any): obj is LibraryExportFileDelimitedType => {
  return obj && typeof obj === 'object' && 'columnSeparator' in obj && 'lineSeparator' in obj;
};

export const isLibraryExportFixedType = (obj: any): obj is LibraryExportFileFixedType => {
  return (
    obj &&
    typeof obj === 'object' &&
    'lineSeparator' in obj &&
    'header' in obj &&
    'sameWidth' in obj
  );
};

export const isLibraryExportJsonType = (obj: any): obj is LibraryExportFileJsonType => {
  return typeof obj?.byteMark === 'boolean';
};

export const isLibraryExportXmlType = (obj: any): obj is LibraryExportFileXmlType => {
  return typeof obj?.byteMark === 'boolean';
};

export const isDagProjectData = (item: DagDatasetData | DagProjectData): item is DagProjectData => {
  return item.type === 'project';
};

export const isChart = (contents?: ContentsType): contents is ChartObject => {
  return contents?.contentsType === OBJECT_TYPE.CHART;
};

export const isChartRef = (contents?: ContentsType): contents is ChartRefObject => {
  return contents?.contentsType === OBJECT_TYPE.CHART_REF;
};

export const isText = (contents?: ContentsType): contents is TextObject => {
  return contents?.contentsType === OBJECT_TYPE.TEXT;
};

export const isShape = (contents?: ContentsType): contents is ShapeObject => {
  return contents?.contentsType === OBJECT_TYPE.SHAPE;
};

export const isSolid = (obj: BackgroundType): obj is BackgroundSolidType => {
  return obj?.bgType === 'SOLID';
};

export const isGradient = (obj: BackgroundType): obj is BackgroundGradientType => {
  return obj?.bgType === 'GRADIENT';
};

export const isImage = (obj: BackgroundType): obj is BackgroundImageType => {
  return obj?.bgType === 'IMAGE';
};

export const isDate = (item: ChatItemType): item is DateChatItemType => {
  return item.type === 'date';
};

export const isUser = (item: ChatItemType): item is UserChatItemType => {
  return item.type === 'user';
};

export const isAssistant = (item: ChatItemType): item is AssistantChatItemType => {
  return item.type === 'assistant';
};

export const isEditHistoryType = (item: OcrHistoryType): item is EditHistoryType => {
  return item.historyType === 'EDIT';
};

export const isResetHistoryType = (item: OcrHistoryType): item is ResetHistoryType => {
  return item.historyType === 'RESET';
};

export const isDeleteHistoryType = (item: OcrHistoryType): item is DeleteHistoryType => {
  return item.historyType === 'DELETE';
};

export const isRestoreHistoryType = (item: OcrHistoryType): item is RestoreHistoryType => {
  return item.historyType === 'RESTORE';
};

export const isAddHistoryType = (item: OcrHistoryType): item is AddHistoryType => {
  return item.historyType === 'ADD';
};

export const isAddColumnHistoryType = (item: OcrHistoryType): item is AddColumnHistoryType => {
  return item.historyType === 'ADD_COLUMN';
};

export const isEditColumnHistoryType = (item: OcrHistoryType): item is EditColumnHistoryType => {
  return item.historyType === 'EDIT_COLUMN';
};

export const isDeleteColumnHistoryType = (
  item: OcrHistoryType
): item is DeleteColumnHistoryType => {
  return item.historyType === 'DELETE_COLUMN';
};

export const isMoveColumnHistoryType = (item: OcrHistoryType): item is MoveColumnHistoryType => {
  return item.historyType === 'MOVE';
};

export const isMergeHistoryType = (item: OcrHistoryType): item is MergeHistoryType => {
  return item.historyType === 'MERGE';
};

export const isPasteHistoryType = (item: OcrHistoryType): item is PasteHistoryType => {
  return item.historyType === 'PASTE';
};
